/**
 * BuchhaltungAbschluss — Jahresabschluss sihirbazı.
 * Spesifikasyon §4 sırası: Prüfungen → GuV-Abschluss → Schlussbilanz +
 * Sperre → Vortrag (Anfangsbilanz Folgejahr). Her adım idempotent;
 * kilitli yıla buchung DB seviyesinde reddedilir.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CalendarCheck, CheckCircle2, XCircle, AlertTriangle, Lock,
  ArrowRightCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/api/AuthContext';
import {
  checkYearEnd, closeGuV, createClosingBalance, carryForward, fmtEUR,
} from '@/lib/buchhaltungApi';

const thisYear = new Date().getFullYear();

function CheckRow({ ok, warn, label, detail }) {
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  const color = ok ? 'text-emerald-600' : warn ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', color)} />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-800">{label}</p>
        {detail && <p className="text-[10px] text-slate-500">{detail}</p>}
      </div>
    </div>
  );
}

export default function BuchhaltungAbschluss() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [year, setYear] = useState(String(thisYear - 0));

  const { data: check, isLoading, refetch } = useQuery({
    queryKey: ['fibu-yearend', year],
    queryFn: () => checkYearEnd(Number(year)),
  });

  const mkMutation = (fn, successMsg) => useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      queryClient.invalidateQueries();
      refetch();
      toast.success(typeof successMsg === 'function' ? successMsg(res) : successMsg);
    },
    onError: (err) => toast.error(err.message),
  });

  /* eslint-disable react-hooks/rules-of-hooks */
  const guvMutation = mkMutation(
    () => closeGuV(Number(year), user?.email),
    (r) => `GuV abgeschlossen — Ergebnis ${fmtEUR(r.result)} auf 7800 gebucht.`
  );
  const closingMutation = mkMutation(
    () => createClosingBalance(Number(year)),
    (r) => `Schlussbilanz erzeugt (${r.count} Positionen) — Jahr ${year} ist GESPERRT.`
  );
  const carryMutation = mkMutation(
    () => carryForward(Number(year), user?.email),
    (r) => `Anfangsbilanz ${Number(year) + 1} vorgetragen (${r.count} Positionen).`
  );
  /* eslint-enable react-hooks/rules-of-hooks */

  const steps = check ? [
    {
      no: 1, title: 'GuV-Konten abschließen',
      desc: `Alle Erlös-/Aufwandskonten werden auf 0 gestellt; Ergebnis (${fmtEUR(check.guvResult)}) geht auf 7800.`,
      done: check.guvClosed,
      blocked: check.locked,
      action: () => {
        if (window.confirm(`GuV ${year} abschließen? Ergebnis ${fmtEUR(check.guvResult)} wird auf 7800 gebucht.`)) {
          guvMutation.mutate();
        }
      },
      pending: guvMutation.isPending,
      cta: 'GuV abschließen',
    },
    {
      no: 2, title: 'Schlussbilanz erzeugen & Jahr sperren',
      desc: 'Bilanzpositionen werden eingefroren. Danach sind KEINE Buchungen in ' + year + ' mehr möglich.',
      done: check.closingCreated,
      blocked: !check.guvClosed,
      danger: true,
      action: () => {
        if (window.confirm(`ACHTUNG: Jahr ${year} wird unwiderruflich gesperrt. Fortfahren?`)) {
          closingMutation.mutate();
        }
      },
      pending: closingMutation.isPending,
      cta: 'Sperren & Schlussbilanz',
    },
    {
      no: 3, title: `Vortrag → Anfangsbilanz ${Number(year) + 1}`,
      desc: 'Schlussbilanzwerte werden als EB-Buchung (01.01.) ins Folgejahr übernommen; 7800 → 2970 Gewinnvortrag.',
      done: check.carried,
      blocked: !check.closingCreated,
      action: () => carryMutation.mutate(),
      pending: carryMutation.isPending,
      cta: 'Vortragen',
    },
  ] : [];

  return (
    <PageShell
      head={(
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <PageHeader icon={<CalendarCheck className="w-5 h-5" />} title="Jahresabschluss" />
            {check?.locked && (
              <Badge className="bg-red-100 text-red-700 border-red-200 gap-1"><Lock className="w-3 h-3" /> Gesperrt</Badge>
            )}
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-11 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[thisYear, thisYear - 1].map((y) => (
                <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    >

      {isLoading && <p className="text-center text-sm text-slate-400 py-10">Prüfe {year}…</p>}

      {check && (
        <>
          {/* Mindestprüfungen */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Prüfungen (Spez. §9)</CardTitle></CardHeader>
            <CardContent className="divide-y divide-slate-50">
              <CheckRow
                ok={check.journalBalanced}
                label="Journal: Sollsumme = Habensumme"
                detail={check.journalBalanced ? 'ausgeglichen' : `Differenz ${fmtEUR(check.journalDiff)}`}
              />
              <CheckRow
                ok={check.afaDone} warn={!check.afaDone}
                label="AfA-Lauf gebucht"
                detail={check.afaDone ? `afa:${year} vorhanden` : 'Noch kein AfA-Lauf — unter Anlagen & AfA buchen (falls Anlagen vorhanden).'}
              />
              <CheckRow
                ok={check.oposMatches} warn={!check.oposMatches}
                label="OPOS Debitoren = Konto 1200"
                detail={`OPOS ${fmtEUR(check.oposSum)} · Konto 1200 ${fmtEUR(check.saldo1200)}${check.oposMatches ? '' : ` · Differenz ${fmtEUR(check.oposDiff)}`}`}
              />
              <CheckRow
                ok
                label={`Vorläufiges Jahresergebnis: ${fmtEUR(check.guvResult)}`}
                detail="aus GuV-Metadaten berechnet"
              />
            </CardContent>
          </Card>

          {/* Adımlar */}
          {steps.map((s) => (
            <Card key={s.no} className={cn(s.done && 'border-emerald-200 bg-emerald-50/40')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center font-black text-sm flex-shrink-0',
                  s.done ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'
                )}>
                  {s.done ? <CheckCircle2 className="w-5 h-5" /> : s.no}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">{s.title}</p>
                  <p className="text-[11px] text-slate-500">{s.desc}</p>
                </div>
                {!s.done && (
                  <Button
                    className={cn(
                      'h-11 text-xs font-bold flex-shrink-0 gap-1.5',
                      s.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-900 hover:bg-slate-800'
                    )}
                    disabled={s.blocked || s.pending}
                    onClick={s.action}
                  >
                    {s.pending ? <Loader2 className="w-4 h-4 animate-spin" />
                      : s.danger ? <Lock className="w-4 h-4" /> : <ArrowRightCircle className="w-4 h-4" />}
                    {s.cta}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}

          <p className="text-[10px] text-slate-400 text-center px-4">
            Nach der Sperre sind Korrekturen in {year} nur noch über Storno-/Korrekturbuchungen
            im Folgejahr möglich. Schlussbilanz {year} = Anfangsbilanz {Number(year) + 1} (Bilanzidentität).
          </p>
        </>
      )}
    </PageShell>
  );
}
